import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";

const NAVY = "#0B1D33";
const OFF_WHITE = "#F4F1EC";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&provider=google`;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: NAVY }}
      >
        <div
          className="w-12 h-12 border-4 rounded-full animate-spin"
          style={{ borderColor: OFF_WHITE, borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        height: "100dvh",
        minHeight: "100dvh",
        backgroundColor: NAVY,
        fontFamily: "'Hanken Grotesk', system-ui, -apple-system, sans-serif",
      }}
      data-testid="landing-welcome"
    >
      {/* Full-bleed background */}
      <img
        src="/cadii/backgrounds/welcome_bg.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />

      {/* Content column — sheet-like, centered */}
      <div className="relative z-10 h-full w-full flex flex-col items-center">
        {/* Lockup at top */}
        <div className="pt-10 sm:pt-14 flex items-center justify-center">
          <img
            src="/cadii/brand/lockup_cadii.png"
            alt="Cadii Golf Golf Club"
            className="w-[150px] h-auto object-contain select-none"
            draggable={false}
          />
        </div>

        <div className="flex-1" />

        {/* Bottom scrim + actions */}
        <div
          className="w-full flex flex-col items-center"
          style={{
            paddingBottom: "max(24px, env(safe-area-inset-bottom))",
            backgroundImage: `linear-gradient(to bottom, rgba(11,29,51,0) 0%, rgba(11,29,51,0.35) 35%, rgba(11,29,51,0.9) 100%)`,
          }}
        >
          <div className="w-full max-w-[420px] px-6 pt-16 flex flex-col gap-3">
            <p
              className="text-center text-[13px] uppercase mb-2"
              style={{
                color: OFF_WHITE,
                fontWeight: 700,
                letterSpacing: "5px",
              }}
              data-testid="landing-tagline"
            >
              PLAY. CAPTURE. SHARE.
            </p>

            <button
              type="button"
              onClick={handleLogin}
              className="w-full rounded-xl transition-transform active:scale-[0.98]"
              style={{
                backgroundColor: NAVY,
                color: OFF_WHITE,
                paddingTop: 15,
                paddingBottom: 15,
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "1px",
              }}
              data-testid="landing-login-btn"
            >
              INICIAR SESIÓN
            </button>

            <button
              type="button"
              onClick={handleLogin}
              className="w-full rounded-xl transition-transform active:scale-[0.98]"
              style={{
                backgroundColor: OFF_WHITE,
                color: NAVY,
                paddingTop: 14,
                paddingBottom: 14,
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: "1px",
              }}
              data-testid="landing-register-btn"
            >
              CREAR CUENTA
            </button>

            <button
              type="button"
              onClick={handleLogin}
              className="w-full rounded-xl flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
              style={{
                backgroundColor: "#FFFFFF",
                color: NAVY,
                paddingTop: 13,
                paddingBottom: 13,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
              data-testid="landing-google-btn"
            >
              <GoogleIcon />
              <span>CONTINUAR CON GOOGLE</span>
            </button>

            <p
              className="text-center mt-3 text-[11px] leading-[17px]"
              style={{
                color: "rgba(244,241,236,0.85)",
                fontWeight: 400,
              }}
            >
              Al continuar, aceptas nuestros{" "}
              <button
                type="button"
                onClick={() => navigate("/terms")}
                className="underline"
                style={{ color: OFF_WHITE, fontWeight: 700 }}
                data-testid="landing-terms-link"
              >
                Términos
              </button>{" "}
              y nuestra{" "}
              <button
                type="button"
                onClick={() => navigate("/privacy")}
                className="underline"
                style={{ color: OFF_WHITE, fontWeight: 700 }}
                data-testid="landing-privacy-link"
              >
                Política de Privacidad
              </button>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

export default LandingPage;
